import * as path from "path";
import { prismaEngineName, PrismaEngineType } from "./utils/prismaEngineName";

export type ResolveOverrides = {
  "@prisma/engines"?: string;
  "@prisma/client"?: string;
  ".prisma/client"?: string;
};

export type PrismaEnginePath = {
  type: PrismaEngineType;
  path: string;
};

type GuessEnginePathsOptions = {
  forceBinary: boolean;
  forceLibrary: boolean;
  resolveOverrides: ResolveOverrides;
};
/**
 * Guesses paths to Prisma engines, taking `resolve`s into account, and defaulting to a hard-coded path
 */
export async function guessEnginePaths({
  forceBinary,
  forceLibrary,
  resolveOverrides,
}: GuessEnginePathsOptions): Promise<{
  queryEngine: PrismaEnginePath | undefined;
}> {
  let queryEngineName: string | undefined;
  let queryEngineType: PrismaEngineType | undefined;
  if (forceLibrary) {
    queryEngineName = await prismaEngineName("query-engine", "library");
    queryEngineType = "library";
  } else if (forceBinary) {
    queryEngineName = await prismaEngineName("query-engine", "binary");
    queryEngineType = "binary";
  } else {
    // Use whatever Client wants
    queryEngineName = undefined;
    queryEngineType = undefined;
  }

  if (!queryEngineName || !queryEngineType) {
    return { queryEngine: undefined };
  }

  let queryEnginePath: string;

  if (resolveOverrides[".prisma/client"]) {
    queryEnginePath = path.resolve(
      resolveOverrides[".prisma/client"],
      `../${queryEngineName}` // This is relative to the `main` field in `.prisma/client`'s package.json
    );
  } else if (resolveOverrides["@prisma/engines"]) {
    queryEnginePath = path.resolve(
      resolveOverrides["@prisma/engines"],
      `../../${queryEngineName}` // This is relative to the `main` field in `@prisma/engine`'s package.json
    );
  } else {
    /**
     * We're using `eval`s for `require.resolve`s here so that Vercel doesn't mess with paths.
     * Without an `eval`, this code becomes statically analyzable by `@vercel/nft`, and so it includes the engines in all deployments.
     */
    let atPrismaEnginesPath;
    try {
      atPrismaEnginesPath = eval("require.resolve('@prisma/engines')");
    } catch (e) {
      throw new Error("Unable to resolve Prisma engine paths. This is a bug.");
    }

    queryEnginePath = path.resolve(
      atPrismaEnginesPath`../../${queryEngineName}` // This is relative to the `main` field in `@prisma/engine`'s package.json
    );
  }

  return {
    queryEngine: { type: queryEngineType, path: queryEnginePath },
  };
}

export type PrismaClientPath = string;

type GuessPrismaClientPathOptions = {
  resolveOverrides: ResolveOverrides;
};
/**
 * Guesses a path to the Prisma Client package, taking `resolve`s into account, and defaulting to a hard-coded path
 */
export function guessPrismaClientPath({
  resolveOverrides,
}: GuessPrismaClientPathOptions): string {
  const prismaClientPath =
    resolveOverrides["@prisma/client"] ||
    eval("require.resolve('@prisma/client')"); // Not using `eval` here results in @vercel/nft treating this as a dependency and so it includes the engines in all deployments. I don't fully understand why at this point.

  return path.resolve(prismaClientPath, "../"); // This is relative to the `main` field in `@prisma/client`'s package.json
}
